/**
 * Phase 9 — AI Site Build Wizard
 *
 * 4-step flow that turns a Company Brief into a fully generated
 * Site Draft, all inside the existing Phase 7 Site Editor data
 * model. The wizard never touches the public site directly —
 * every AI write lands in the Draft and the operator must use
 * the normal Preview/Publish path to ship.
 *
 * Steps:
 *   1. Brief      — fill structured company info (or edit existing)
 *   2. Plan       — generate a SitePlan, review/edit, confirm
 *   3. Generate   — kick off full-site generation, watch per-section progress
 *   4. Review     — link to the regular editor for fine-tuning
 *
 * The page is self-contained. Polling is done in-place; no extra
 * BackgroundJob component needed. State persists across re-renders
 * via plain useState.
 */
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {aiSiteApi, sitesApi} from '../lib/api-resources';
import {ApiError} from '../lib/api';
import type {
  BriefGoal,
  BriefPayload,
  BriefResponse,
  BriefTone,
  GeneratePlanResponse,
  PlanPage,
  PlanSection,
  PlanSectionType,
  SiteJobStatusResponse,
  SitePlan,
} from '../lib/types';
import './AiBuildPage.css';

type Step = 'brief' | 'plan' | 'generate' | 'review';

const TONE_OPTIONS: {value: BriefTone; tKey: string}[] = [
  {value: 'professional', tKey: 'aiBuild.toneProfessional'},
  {value: 'friendly', tKey: 'aiBuild.toneFriendly'},
  {value: 'technical', tKey: 'aiBuild.toneTechnical'},
  {value: 'playful', tKey: 'aiBuild.tonePlayful'},
  {value: 'authoritative', tKey: 'aiBuild.toneAuthoritative'},
  {value: 'warm', tKey: 'aiBuild.toneWarm'},
  {value: 'minimal', tKey: 'aiBuild.toneMinimal'},
];

const GOAL_OPTIONS: {value: BriefGoal; tKey: string}[] = [
  {value: 'generate_leads', tKey: 'aiBuild.primaryGoalGenerateLeads'},
  {value: 'showcase_products', tKey: 'aiBuild.primaryGoalShowcaseProducts'},
  {value: 'build_trust', tKey: 'aiBuild.primaryGoalBuildTrust'},
  {value: 'inform_visitors', tKey: 'aiBuild.primaryGoalInformVisitors'},
  {value: 'drive_signups', tKey: 'aiBuild.primaryGoalDriveSignups'},
  {value: 'support_customers', tKey: 'aiBuild.primaryGoalSupportCustomers'},
];

const SECTION_TYPE_KEYS: Record<PlanSectionType, string> = {
  hero: 'SECTION_TYPE.hero',
  features: 'SECTION_TYPE.features',
  about: 'SECTION_TYPE.about',
  products: 'SECTION_TYPE.products',
  cta: 'SECTION_TYPE.cta',
  faq: 'SECTION_TYPE.faq',
};

const STEP_KEYS: {key: Step; tKey: string}[] = [
  {key: 'brief', tKey: 'aiBuild.stepBrief'},
  {key: 'plan', tKey: 'aiBuild.stepPlan'},
  {key: 'generate', tKey: 'aiBuild.stepGenerate'},
  {key: 'review', tKey: 'aiBuild.stepReview'},
];

const TERMINAL_JOB_STATUSES: ReadonlyArray<string> = [
  'SUCCESS',
  'PARTIAL',
  'FAILED',
  'CANCELLED',
];

function emptyBrief(): BriefPayload {
  return {
    company_name: '',
    brand_name: '',
    industry: '',
    company_description: '',
    products_services: '',
    target_customers: '',
    markets: '',
    countries: '',
    competitive_advantages: '',
    certifications: '',
    years_in_business: null,
    contact_email: '',
    contact_phone: '',
    address: '',
    // Phase 9.1 — the operator's primary CTA destination. Empty
    // string here means "I haven't picked one" — the server falls
    // back to ``/contact`` so the model always has a real answer.
    primary_cta_url: '',
    languages: [],
    preferred_tone: null,
    primary_goal: null,
    reference_notes: '',
    raw_materials: '',
  };
}

function errMsg(e: unknown): string {
  if (e instanceof ApiError) return `${e.code}: ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

function genIdempotencyKey(prefix: string): string {
  // 16-char key, enough entropy to be effectively unique per click
  // while short enough to be readable in the logs.
  const rand = Math.random().toString(36).slice(2, 10);
  return `${prefix}-${Date.now().toString(36)}-${rand}`;
}

export default function AiBuildPage() {
  const {siteId} = useParams<{siteId: string}>();
  const navigate = useNavigate();
  const {t} = useTranslation();
  const [step, setStep] = useState<Step>('brief');

  // Brief
  const [briefResp, setBriefResp] = useState<BriefResponse | null>(null);
  const [brief, setBrief] = useState<BriefPayload>(emptyBrief());
  const [briefLoading, setBriefLoading] = useState(true);
  const [briefSaving, setBriefSaving] = useState(false);
  const [briefErr, setBriefErr] = useState<string | null>(null);

  // Plan
  const [planResp, setPlanResp] = useState<GeneratePlanResponse | null>(null);
  const [planEditing, setPlanEditing] = useState<SitePlan | null>(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [planErr, setPlanErr] = useState<string | null>(null);
  const [operatorNotes, setOperatorNotes] = useState<string>('');
  // Note: the plan idempotency key is **regenerated on every click**
  // of "Generate plan" / "Regenerate plan" (see runPlan). The old
  // design froze it on mount via useState, which made every
  // subsequent click return the same job — defeating the whole
  // point of the "Regenerate" button. The server is happy to take
  // a fresh key per click; the only thing the key is for is
  // network-retry idempotency, which is handled at the call site.

  // Generate
  const [genJobId, setGenJobId] = useState<string | null>(null);
  const [genJob, setGenJob] = useState<SiteJobStatusResponse | null>(null);
  const [genErr, setGenErr] = useState<string | null>(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genKey, setGenKey] = useState<string>(() =>
    genIdempotencyKey('gen')
  );
  const [humanPolicy, setHumanPolicy] = useState<'protect' | 'overwrite'>(
    'protect'
  );

  // UI
  const [topError, setTopError] = useState<string | null>(null);
  const [draftRevision, setDraftRevision] = useState<number>(0);

  // ===== Load brief + draft revision on mount =====
  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    (async () => {
      setBriefLoading(true);
      setBriefErr(null);
      try {
        const [b, snap] = await Promise.all([
          aiSiteApi.getBrief(siteId),
          sitesApi.editor.snapshot(siteId),
        ]);
        if (!alive) return;
        setBriefResp(b);
        setBrief({...emptyBrief(), ...b.brief});
        setDraftRevision(snap.draft_revision);
      } catch (e) {
        if (alive) setBriefErr(errMsg(e));
      } finally {
        if (alive) setBriefLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [siteId]);

  // ===== Brief: save =====
  // Phase 9.1 fix: this function now re-throws on error so
  // ``goToPlan`` can reliably catch the failure and block the
  // navigation. The old version swallowed the error into state
  // only, so ``goToPlan`` could continue to Plan even when the save
  // 409'd (e.g. version conflict from another tab).
  const saveBrief = useCallback(async (): Promise<void> => {
    if (!siteId || !briefResp) return;
    setBriefSaving(true);
    setBriefErr(null);
    try {
      const resp = await aiSiteApi.upsertBrief(siteId, {
        expected_version: briefResp.version,
        brief,
      });
      setBriefResp(resp);
      setBrief({...emptyBrief(), ...resp.brief});
    } catch (e) {
      setBriefErr(errMsg(e));
      // Re-throw — the caller's catch decides whether to block
      // navigation. We do NOT swallow errors into the state only.
      throw e;
    } finally {
      setBriefSaving(false);
    }
  }, [siteId, briefResp, brief]);

  // ===== Brief validation summary =====
  const briefValid = useMemo(() => {
    // Minimal gate: at least a company name AND some context. The
    // AI will use what's there; empty fields simply produce empty
    // generated content. We still want a name to anchor "who".
    return (brief.company_name ?? '').trim().length > 0;
  }, [brief.company_name]);

  // ===== Step 1 → 2 =====
  // Phase 9.1 fix: navigation to Plan now requires a successful
  // saveBrief. If the save fails (network, 409 version conflict,
  // etc.) we stay on Step 1 — never silently advance to a Plan
  // step that won't be able to read the new brief.
  const goToPlan = useCallback(async () => {
    if (!siteId) return;
    if (!briefValid) {
      setTopError('请至少填写公司名称。');
      return;
    }
    setTopError(null);
    if (briefResp) {
      try {
        await saveBrief();
      } catch (e) {
        setTopError('资料保存失败：' + errMsg(e));
        return;
      }
    }
    setStep('plan');
  }, [siteId, briefValid, briefResp, saveBrief]);

  // ===== Plan: generate / regenerate =====
  // Phase 9.1 fix: a fresh idempotency key is generated on every
  // click of "Generate plan" / "Regenerate plan". The old design
  // froze the key on mount, so every subsequent click returned the
  // same job — defeating the Regenerate button.
  const runPlan = useCallback(async () => {
    if (!siteId) return;
    setPlanBusy(true);
    setPlanErr(null);
    const freshKey = genIdempotencyKey('plan');
    try {
      const resp = await aiSiteApi.generatePlan(siteId, {
        operator_notes: operatorNotes.trim() || null,
        idempotency_key: freshKey,
      });
      setPlanResp(resp);
      if (resp.plan) {
        setPlanEditing(structuredClone(resp.plan));
      } else {
        setPlanErr(
          `${resp.status}${resp.error_code ? ' / ' + resp.error_code : ''}: ${
            resp.error_message ?? 'no plan returned'
          }`
        );
      }
    } catch (e) {
      setPlanErr(errMsg(e));
    } finally {
      setPlanBusy(false);
    }
  }, [siteId, operatorNotes]);

  // ===== Plan: confirm =====
  // Phase 9.1 fix: confirmPlan now reads the just-bumped
  // ``draft_revision`` from the server response and patches it into
  // local state. Without this, the next /ai-site/generate call
  // sends the *stale* revision and lands in 409.
  const confirmPlan = useCallback(async () => {
    if (!siteId || !planResp || !planEditing) return;
    setPlanBusy(true);
    setPlanErr(null);
    try {
      const resp = await aiSiteApi.confirmPlan(siteId, {
        expected_plan_job_id: planResp.job_id,
        expected_draft_revision: draftRevision,
        plan: planEditing,
      });
      // Pull the new draft_revision from the response (Phase 9.1
      // contract). Fall back to a refetch if the server is older
      // and didn't include it — defensive.
      if (typeof resp.draft_revision === 'number') {
        setDraftRevision(resp.draft_revision);
      } else {
        try {
          const snap = await sitesApi.editor.snapshot(siteId);
          setDraftRevision(snap.draft_revision);
        } catch {
          /* leave revision alone; user will see 409 if stale */
        }
      }
      setStep('generate');
    } catch (e) {
      setPlanErr(errMsg(e));
    } finally {
      setPlanBusy(false);
    }
  }, [siteId, planResp, planEditing, draftRevision]);

  // ===== Plan editing helpers =====
  const updatePage = (idx: number, patch: Partial<PlanPage>) => {
    if (!planEditing) return;
    const next = {...planEditing};
    const pages = [...next.recommended_pages];
    pages[idx] = {...pages[idx], ...patch};
    next.recommended_pages = pages;
    setPlanEditing(next);
  };
  const removePage = (idx: number) => {
    if (!planEditing) return;
    const next = {...planEditing};
    next.recommended_pages = next.recommended_pages.filter((_, i) => i !== idx);
    setPlanEditing(next);
  };
  const addPage = () => {
    if (!planEditing) return;
    const next = {...planEditing};
    next.recommended_pages = [
      ...next.recommended_pages,
      {
        role: 'CUSTOM',
        slug: `extra-${next.recommended_pages.length + 1}`,
        title: 'New page',
        sections: [],
      },
    ];
    setPlanEditing(next);
  };
  const updateSection = (
    pageIdx: number,
    secIdx: number,
    patch: Partial<PlanSection>
  ) => {
    if (!planEditing) return;
    const next = {...planEditing};
    const pages = [...next.recommended_pages];
    const sections = [...pages[pageIdx].sections];
    sections[secIdx] = {...sections[secIdx], ...patch};
    pages[pageIdx] = {...pages[pageIdx], sections};
    next.recommended_pages = pages;
    setPlanEditing(next);
  };
  const removeSection = (pageIdx: number, secIdx: number) => {
    if (!planEditing) return;
    const next = {...planEditing};
    const pages = [...next.recommended_pages];
    pages[pageIdx] = {
      ...pages[pageIdx],
      sections: pages[pageIdx].sections.filter((_, i) => i !== secIdx),
    };
    next.recommended_pages = pages;
    setPlanEditing(next);
  };
  const addSection = (pageIdx: number) => {
    if (!planEditing) return;
    const next = {...planEditing};
    const pages = [...next.recommended_pages];
    pages[pageIdx] = {
      ...pages[pageIdx],
      sections: [
        ...pages[pageIdx].sections,
        {type: 'features', notes: '', ai_fill: true},
      ],
    };
    next.recommended_pages = pages;
    setPlanEditing(next);
  };

  // ===== Step 3 → 3 =====
  const startGenerate = useCallback(async () => {
    if (!siteId || !planResp) return;
    setGenBusy(true);
    setGenErr(null);
    try {
      const resp = await aiSiteApi.generateFullSite(siteId, {
        expected_plan_job_id: planResp.job_id,
        expected_draft_revision: draftRevision,
        idempotency_key: genKey,
        human_modified_policy: humanPolicy,
      });
      setGenJobId(resp.job_id);
      setGenJob({
        site_id: siteId,
        job_id: resp.job_id,
        job_kind: 'SITE_AI_JOB_KIND_SITE_GENERATE',
        status: resp.status,
        phases: resp.phases,
        plan: null,
        site_context: null,
        sections: [],
        started_at: resp.started_at ?? null,
        completed_at: null,
        error_code: null,
        error_message: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } catch (e) {
      setGenErr(errMsg(e));
    } finally {
      setGenBusy(false);
    }
  }, [siteId, planResp, draftRevision, genKey, humanPolicy]);

  // ===== Generate: poll =====
  useEffect(() => {
    if (!siteId || !genJobId) return;
    if (genJob && TERMINAL_JOB_STATUSES.includes(genJob.status)) return;
    let alive = true;
    const timer = window.setTimeout(async () => {
      try {
        const j = await aiSiteApi.getJob(siteId, genJobId);
        if (alive) setGenJob(j);
      } catch (e) {
        if (alive) setGenErr(errMsg(e));
      }
    }, 2000);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [siteId, genJobId, genJob]);

  // ===== Generate: cancel =====
  const cancelGenerate = useCallback(async () => {
    if (!siteId || !genJobId) return;
    setGenBusy(true);
    try {
      const j = await aiSiteApi.cancelJob(siteId, genJobId);
      setGenJob(j);
    } catch (e) {
      setGenErr(errMsg(e));
    } finally {
      setGenBusy(false);
    }
  }, [siteId, genJobId]);

  // ===== Generate: retry =====
  // Phase 9.1 fix: the retry response is a brand-new job. The
  // wizard switches to it immediately and the polling effect picks
  // it up; the old job is left in the lineage for audit (and is
  // reachable via the Jobs panel). We also stash a small "retry
  // notice" so the UI can show "Retry job xxx — retrying N failed
  // sections" right after the click.
  const [retryNotice, setRetryNotice] = useState<{
    fromJobId: string;
    toJobId: string;
    retriedCount: number;
  } | null>(null);
  const retryGenerate = useCallback(async () => {
    if (!siteId || !genJobId) return;
    setGenBusy(true);
    setGenErr(null);
    // Count what we're about to retry (for the notice).
    const retriedCount =
      genJob?.sections.filter(
        (s) => s.status === 'FAILED' || s.status === 'SKIPPED'
      ).length ?? 0;
    try {
      const resp = await aiSiteApi.retryJob(siteId, genJobId);
      setGenJobId(resp.job_id);
      setGenKey(genIdempotencyKey('gen'));
      setGenJob({
        site_id: siteId,
        job_id: resp.job_id,
        job_kind: 'SITE_AI_JOB_KIND_SITE_GENERATE',
        status: resp.status,
        phases: resp.phases,
        plan: null,
        site_context: null,
        sections: [],
        started_at: resp.started_at ?? null,
        completed_at: null,
        error_code: null,
        error_message: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      setRetryNotice({
        fromJobId: genJobId,
        toJobId: resp.job_id,
        retriedCount,
      });
    } catch (e) {
      setGenErr(errMsg(e));
    } finally {
      setGenBusy(false);
    }
  }, [siteId, genJobId, genJob]);

  if (!siteId) {
    return <div className="page">{t('aiBuild.missingSiteId')}</div>;
  }

  const stepIdx = STEP_KEYS.findIndex((s) => s.key === step);

  return (
    <div className="page ai-build-page">
      <header className="ai-build-header">
        <button className="btn-link" onClick={() => navigate(`/sites/${siteId}/editor`)}>
          ← {t('aiBuild.backToEditor')}
        </button>
        <h1>{t('aiBuild.title')}</h1>
        <p className="muted small">
          {t('aiBuild.intro')}
        </p>
      </header>

      <ol className="ai-build-steps">
        {STEP_KEYS.map((s, i) => (
          <li
            key={s.key}
            className={
              'ai-build-step ' +
              (i === stepIdx ? 'active ' : i < stepIdx ? 'done ' : '') +
              (i === stepIdx ? s.key : '')
            }
            onClick={() => {
              if (i < stepIdx) setStep(s.key);
            }}
          >
            <span className="ai-build-step-num">{i + 1}</span>
            <span className="ai-build-step-title">{t(s.tKey + '.title')}</span>
            <span className="ai-build-step-sub muted small">{t(s.tKey + '.sub')}</span>
          </li>
        ))}
      </ol>

      {topError && (
        <div className="callout callout-error">
          {topError}
          <button className="btn-link" onClick={() => setTopError(null)}>
            知道了
          </button>
        </div>
      )}

      {step === 'brief' && (
        <BriefStep
          brief={brief}
          setBrief={setBrief}
          resp={briefResp}
          loading={briefLoading}
          err={briefErr}
          saving={briefSaving}
          valid={briefValid}
          onSave={saveBrief}
          onContinue={goToPlan}
        />
      )}

      {step === 'plan' && (
        <PlanStep
          operatorNotes={operatorNotes}
          setOperatorNotes={setOperatorNotes}
          planResp={planResp}
          planEditing={planEditing}
          setPlanEditing={setPlanEditing}
          err={planErr}
          busy={planBusy}
          onRunPlan={runPlan}
          onConfirm={confirmPlan}
          onBack={() => setStep('brief')}
          updatePage={updatePage}
          removePage={removePage}
          addPage={addPage}
          updateSection={updateSection}
          removeSection={removeSection}
          addSection={addSection}
        />
      )}

      {step === 'generate' && (
        <GenerateStep
          job={genJob}
          err={genErr}
          busy={genBusy}
          humanPolicy={humanPolicy}
          setHumanPolicy={setHumanPolicy}
          retryNotice={retryNotice}
          onStart={startGenerate}
          onCancel={cancelGenerate}
          onRetry={retryGenerate}
          onContinue={() => setStep('review')}
          onBack={() => setStep('plan')}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          siteId={siteId}
          job={genJob}
          onOpenEditor={() => navigate(`/sites/${siteId}/editor`)}
        />
      )}
    </div>
  );
}

// =====================================================================
// Step 1 — Brief
// =====================================================================

interface BriefStepProps {
  brief: BriefPayload;
  setBrief: (b: BriefPayload) => void;
  resp: BriefResponse | null;
  loading: boolean;
  err: string | null;
  saving: boolean;
  valid: boolean;
  onSave: () => void;
  onContinue: () => void;
}

function BriefStep({
  brief,
  setBrief,
  resp,
  loading,
  err,
  saving,
  valid,
  onSave,
  onContinue,
}: BriefStepProps) {
  const {t} = useTranslation();
  const set = <K extends keyof BriefPayload>(k: K, v: BriefPayload[K]) =>
    setBrief({...brief, [k]: v});

  if (loading) {
    return <div className="card">{t('aiBuild.loadingBrief')}</div>;
  }

  return (
    <div className="card ai-build-card">
      <div className="card-title">
        <span>{t('aiBuild.companyBrief')}</span>
        {resp && (
          <span className="muted small">
            v{resp.version} · {resp.status}
          </span>
        )}
      </div>

      {err && <div className="callout callout-error">{err}</div>}

      <div className="ai-build-grid">
        <Field
          label={t('aiBuild.companyName')}
          required
          value={brief.company_name ?? ''}
          onChange={(v) => set('company_name', v)}
        />
        <Field
          label={t('aiBuild.brandName')}
          value={brief.brand_name ?? ''}
          onChange={(v) => set('brand_name', v)}
        />
        <Field
          label={t('aiBuild.industry')}
          value={brief.industry ?? ''}
          onChange={(v) => set('industry', v)}
        />
        <Field
          label={t('aiBuild.yearsInBusiness')}
          type="number"
          value={brief.years_in_business == null ? '' : String(brief.years_in_business)}
          onChange={(v) =>
            set('years_in_business', v === '' ? null : Number(v))
          }
        />
        <Field
          label={t('aiBuild.contactEmail')}
          type="email"
          value={brief.contact_email ?? ''}
          onChange={(v) => set('contact_email', v)}
        />
        <Field
          label={t('aiBuild.contactPhone')}
          value={brief.contact_phone ?? ''}
          onChange={(v) => set('contact_phone', v)}
        />
        <Field
          label={t('aiBuild.address')}
          wide
          value={brief.address ?? ''}
          onChange={(v) => set('address', v)}
        />
        <Field
          label={t('aiBuild.primaryCtaUrl')}
          wide
          value={brief.primary_cta_url ?? ''}
          onChange={(v) => set('primary_cta_url', v)}
          hint={t('aiBuild.primaryCtaUrlHint')}
        />
        <Area
          label={t('aiBuild.companyDescription')}
          wide
          value={brief.company_description ?? ''}
          onChange={(v) => set('company_description', v)}
          hint={t('aiBuild.companyDescriptionHint')}
        />
        <Area
          label={t('aiBuild.productsServices')}
          wide
          value={brief.products_services ?? ''}
          onChange={(v) => set('products_services', v)}
        />
        <Area
          label={t('aiBuild.targetCustomer')}
          wide
          value={brief.target_customers ?? ''}
          onChange={(v) => set('target_customers', v)}
        />
        <Area
          label={t('aiBuild.marketsCountries')}
          wide
          value={brief.markets ?? ''}
          onChange={(v) => set('markets', v)}
        />
        <Field
          label={t('aiBuild.countriesRaw')}
          wide
          value={brief.countries ?? ''}
          onChange={(v) => set('countries', v)}
          hint={t('aiBuild.countriesRawHint')}
        />
        <Area
          label={t('aiBuild.competitiveAdvantages')}
          wide
          value={brief.competitive_advantages ?? ''}
          onChange={(v) => set('competitive_advantages', v)}
        />
        <Area
          label={t('aiBuild.certifications')}
          wide
          value={brief.certifications ?? ''}
          onChange={(v) => set('certifications', v)}
          hint={t('aiBuild.certificationsHint')}
        />
        <Select
          label={t('aiBuild.tone')}
          value={brief.preferred_tone ?? ''}
          options={[{value: '', label: t('aiBuild.tonePickOne')}, ...TONE_OPTIONS.map((o) => ({value: o.value, label: t(o.tKey)}))]}
          onChange={(v) => set('preferred_tone', (v || null) as BriefTone | null)}
        />
        <Select
          label={t('aiBuild.primaryGoal')}
          value={brief.primary_goal ?? ''}
          options={[{value: '', label: t('aiBuild.tonePickOne')}, ...GOAL_OPTIONS.map((o) => ({value: o.value, label: t(o.tKey)}))]}
          onChange={(v) => set('primary_goal', (v || null) as BriefGoal | null)}
        />
        <LanguagesField
          value={brief.languages ?? []}
          onChange={(v) => set('languages', v)}
        />
        <Area
          label={t('aiBuild.referenceNotes')}
          wide
          value={brief.reference_notes ?? ''}
          onChange={(v) => set('reference_notes', v)}
          hint={t('aiBuild.referenceNotesHint')}
        />
        <Area
          label={t('aiBuild.rawMaterials')}
          wide
          value={brief.raw_materials ?? ''}
          onChange={(v) => set('raw_materials', v)}
          hint={t('aiBuild.rawMaterialsHint')}
        />
      </div>

      <div className="ai-build-actions">
        <button
          className="btn btn-secondary"
          onClick={onSave}
          disabled={saving || !resp}
        >
          {saving ? t('common.saving') : t('aiBuild.saveBrief')}
        </button>
        <button
          className="btn btn-primary"
          onClick={onContinue}
          disabled={!valid}
          title={!valid ? `${t('aiBuild.companyName')}` : ''}
        >
          {t('aiBuild.continueGeneratePlan')}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Step 2 — Plan
// =====================================================================

interface PlanStepProps {
  operatorNotes: string;
  setOperatorNotes: (v: string) => void;
  planResp: GeneratePlanResponse | null;
  planEditing: SitePlan | null;
  setPlanEditing: (p: SitePlan | null) => void;
  err: string | null;
  busy: boolean;
  onRunPlan: () => void;
  onConfirm: () => void;
  onBack: () => void;
  updatePage: (idx: number, patch: Partial<PlanPage>) => void;
  removePage: (idx: number) => void;
  addPage: () => void;
  updateSection: (
    pageIdx: number,
    secIdx: number,
    patch: Partial<PlanSection>
  ) => void;
  removeSection: (pageIdx: number, secIdx: number) => void;
  addSection: (pageIdx: number) => void;
}

function PlanStep({
  operatorNotes,
  setOperatorNotes,
  planResp,
  planEditing,
  err,
  busy,
  onRunPlan,
  onConfirm,
  onBack,
  updatePage,
  removePage,
  addPage,
  updateSection,
  removeSection,
  addSection,
}: PlanStepProps) {
  const {t} = useTranslation();
  return (
    <div className="ai-build-card-stack">
      <div className="card ai-build-card">
        <div className="card-title">
          <span>{t('aiBuild.generatePlan')}</span>
          {planResp && (
            <span className="muted small">任务 {planResp.job_id.slice(0, 8)}</span>
          )}
        </div>
        {err && <div className="callout callout-error">{err}</div>}
        <p className="muted small">
          AI 会根据企业资料推荐一个页面 + 区块结构。确认前你可以增删改页面和区块。
        </p>
        <div className="ai-build-grid">
          <Area
            label={t('aiBuild.operatorNotes')}
            wide
            value={operatorNotes}
            onChange={setOperatorNotes}
            hint={t('aiBuild.operatorNotesHint')}
          />
        </div>
        <div className="ai-build-actions">
          <button className="btn btn-secondary" onClick={onBack}>
            ← {t('aiBuild.back')}
          </button>
          <button
            className="btn btn-primary"
            onClick={onRunPlan}
            disabled={busy}
          >
            {busy ? t('aiBuild.generatingPlan') : planEditing ? t('aiBuild.regeneratePlan') : t('aiBuild.generatePlan')}
          </button>
        </div>
      </div>

      {planEditing && (
        <div className="card ai-build-card">
          <div className="card-title">
            <span>{t('aiBuild.planStructure')}</span>
            <span className="muted small">
              共 {planEditing.recommended_pages.length} 个页面
            </span>
          </div>

          <div className="plan-pages">
            {planEditing.recommended_pages.map((page, pIdx) => (
              <div key={`${page.slug}-${pIdx}`} className="plan-page">
                <div className="plan-page-head">
                  <input
                    className="plan-input plan-input-title"
                    value={page.title}
                    onChange={(e) => updatePage(pIdx, {title: e.target.value})}
                  />
                  <span className={`pill pill-${page.role.toLowerCase()}`}>
                    {page.role}
                  </span>
                  <input
                    className="plan-input plan-input-slug"
                    value={page.slug}
                    onChange={(e) => updatePage(pIdx, {slug: e.target.value})}
                    placeholder="路径"
                  />
                  <button
                    className="btn-link danger"
                    onClick={() => removePage(pIdx)}
                    title={t('aiBuild.removePage')}
                  >
                    ×
                  </button>
                </div>
                <input
                  className="plan-input plan-input-notes"
                  placeholder={t('aiBuild.pageNotes')}
                  value={page.notes ?? ''}
                  onChange={(e) => updatePage(pIdx, {notes: e.target.value})}
                />
                <ul className="plan-sections">
                  {page.sections.map((sec, sIdx) => (
                    <li key={`${sec.type}-${sIdx}`} className="plan-section">
                      <select
                        className="plan-input"
                        value={sec.type}
                        onChange={(e) =>
                          updateSection(pIdx, sIdx, {
                            type: e.target.value as PlanSectionType,
                          })
                        }
                      >
                        {(Object.keys(SECTION_TYPE_KEYS) as PlanSectionType[]).map(
                          (k) => (
                            <option key={k} value={k}>
                              {t(SECTION_TYPE_KEYS[k])}
                            </option>
                          )
                        )}
                      </select>
                      <input
                        className="plan-input"
                        placeholder={t('aiBuild.sectionNotes')}
                        value={sec.notes ?? ''}
                        onChange={(e) =>
                          updateSection(pIdx, sIdx, {notes: e.target.value})
                        }
                      />
                      <label className="plan-toggle">
                        <input
                          type="checkbox"
                          checked={sec.ai_fill}
                          onChange={(e) =>
                            updateSection(pIdx, sIdx, {ai_fill: e.target.checked})
                          }
                        />
                        {t('aiBuild.aiFill')}
                      </label>
                      <button
                        className="btn-link danger"
                        onClick={() => removeSection(pIdx, sIdx)}
                        title={t('aiBuild.removeSection')}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  className="btn-link"
                  onClick={() => addSection(pIdx)}
                >
                  + {t('aiBuild.addSection')}
                </button>
              </div>
            ))}
          </div>

          <div className="ai-build-actions">
            <button className="btn-link" onClick={addPage}>
              + {t('aiBuild.addPage')}
            </button>
          </div>

          {planEditing.content_strategy && (
            <div className="callout callout-info">
              <strong>{t('aiBuild.contentStrategy')}：</strong> {planEditing.content_strategy}
            </div>
          )}
          {planEditing.tone && (
            <div className="callout callout-info">
              <strong>{t('aiBuild.toneLabel')}：</strong> {planEditing.tone}
              {planEditing.primary_cta && (
                <>
                  <br />
                  <strong>{t('aiBuild.primaryCta')}：</strong> {planEditing.primary_cta}
                </>
              )}
            </div>
          )}
          {planEditing.key_messages.length > 0 && (
            <div className="callout callout-info">
              <strong>{t('aiBuild.keyMessages')}：</strong>
              <ul>
                {planEditing.key_messages.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="ai-build-actions">
            <button
              className="btn btn-primary"
              onClick={onConfirm}
              disabled={busy || planEditing.recommended_pages.length === 0}
            >
              {busy ? t('aiBuild.confirmingPlan') : t('aiBuild.confirmPlan')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Step 3 — Generate
// =====================================================================

interface GenerateStepProps {
  job: SiteJobStatusResponse | null;
  err: string | null;
  busy: boolean;
  humanPolicy: 'protect' | 'overwrite';
  setHumanPolicy: (p: 'protect' | 'overwrite') => void;
  retryNotice: {
    fromJobId: string;
    toJobId: string;
    retriedCount: number;
  } | null;
  onStart: () => void;
  onCancel: () => void;
  onRetry: () => void;
  onContinue: () => void;
  onBack: () => void;
}

function GenerateStep({
  job,
  err,
  busy,
  humanPolicy,
  setHumanPolicy,
  retryNotice,
  onStart,
  onCancel,
  onRetry,
  onContinue,
  onBack,
}: GenerateStepProps) {
  const {t} = useTranslation();
  const started = !!job;
  const isRunning = started && !TERMINAL_JOB_STATUSES.includes(job!.status);
  const isTerminal = started && TERMINAL_JOB_STATUSES.includes(job!.status);

  // Section progress breakdown (Phase 9.1). Previously the UI
  // collapsed success+protected into a single percentage bar —
  // which made a 7/13 PARTIAL job look 100% green. Show every
  // status class explicitly so the operator sees the real picture.
  const sectionsTotal = job?.sections.length ?? 0;
  const sectionsSuccess =
    job?.sections.filter((s) => s.status === 'SUCCESS').length ?? 0;
  const sectionsFailed =
    job?.sections.filter((s) => s.status === 'FAILED').length ?? 0;
  const sectionsProtected =
    job?.sections.filter((s) => s.human_protected).length ?? 0;
  const sectionsRunning =
    job?.sections.filter((s) => s.status === 'RUNNING').length ?? 0;
  const sectionsPending =
    job?.sections.filter((s) => s.status === 'PENDING').length ?? 0;
  const sectionsSkipped =
    job?.sections.filter((s) => s.status === 'SKIPPED').length ?? 0;

  // Progress bar only shows "real" completion (SUCCESS +
  // HUMAN_PROTECTED — both are "this section is in a useful
  // state"). FAILED / RUNNING / PENDING / SKIPPED don't count
  // toward completion. That way PARTIAL jobs look partial, not
  // full-green.
  const doneCount = sectionsSuccess + sectionsProtected;
  const overallPct = sectionsTotal
    ? Math.round((doneCount / sectionsTotal) * 100)
    : isRunning
    ? 5
    : 0;

  return (
    <div className="card ai-build-card">
      <div className="card-title">
        <span>{t('aiBuild.generateFullSite')}</span>
        {job && <span className="muted small">任务 {job.job_id.slice(0, 8)}</span>}
      </div>

      {err && <div className="callout callout-error">{err}</div>}

      {retryNotice && (
        <div className="callout callout-info">
          {t('aiBuild.retryNotice', {
            newId: retryNotice.toJobId.slice(0, 8),
            n: retryNotice.retriedCount,
            oldId: retryNotice.fromJobId.slice(0, 8),
          })}
        </div>
      )}

      {!started && (
        <>
          <p className="muted small">{t('aiBuild.policyHelpHint')}</p>
          <div className="callout callout-warn">
            <strong>{t('aiBuild.policyHelp')}：</strong>
            <div className="plan-toggle-row">
              <label>
                <input
                  type="radio"
                  name="policy"
                  checked={humanPolicy === 'protect'}
                  onChange={() => setHumanPolicy('protect')}
                />
                <strong>{t('aiBuild.protect')}</strong>（{t('aiBuild.protectHint')}）
              </label>
              <label>
                <input
                  type="radio"
                  name="policy"
                  checked={humanPolicy === 'overwrite'}
                  onChange={() => setHumanPolicy('overwrite')}
                />
                <strong>{t('aiBuild.overwrite')}</strong>（{t('aiBuild.overwriteHint')}）
              </label>
            </div>
          </div>
          <div className="ai-build-actions">
            <button className="btn btn-secondary" onClick={onBack}>
              ← {t('aiBuild.back')}
            </button>
            <button className="btn btn-primary" onClick={onStart} disabled={busy}>
              {busy ? t('aiBuild.generating') : t('aiBuild.generateFullSite')}
            </button>
          </div>
        </>
      )}

      {started && (
        <>
          <div className="gen-status">
            <span className={`gen-pill gen-pill-${job!.status.toLowerCase()}`}>
              {job!.status}
            </span>
            {isRunning && (
              <span className="muted small">每 2 秒刷新一次状态…</span>
            )}
          </div>

          <div className="gen-progress">
            <div
              className={`gen-progress-bar gen-progress-${job!.status.toLowerCase()}`}
              style={{width: `${overallPct}%`}}
            />
            <div className="gen-progress-text">
              {doneCount}/{sectionsTotal} 区块已就绪
              {sectionsTotal > 0 && `（${overallPct}%）`}
            </div>
          </div>

          {sectionsTotal > 0 && (
            <ul className="gen-breakdown">
              <li className="gen-stat gen-stat-success">
                <span className="gen-stat-num">{sectionsSuccess}</span>
                <span className="gen-stat-label">已生成</span>
              </li>
              <li className="gen-stat gen-stat-protected">
                <span className="gen-stat-num">{sectionsProtected}</span>
                <span className="gen-stat-label">已保护</span>
              </li>
              <li className="gen-stat gen-stat-failed">
                <span className="gen-stat-num">{sectionsFailed}</span>
                <span className="gen-stat-label">失败</span>
              </li>
              {sectionsRunning > 0 && (
                <li className="gen-stat gen-stat-running">
                  <span className="gen-stat-num">{sectionsRunning}</span>
                  <span className="gen-stat-label">执行中</span>
                </li>
              )}
              {sectionsPending > 0 && (
                <li className="gen-stat gen-stat-pending">
                  <span className="gen-stat-num">{sectionsPending}</span>
                  <span className="gen-stat-label">等待中</span>
                </li>
              )}
              {sectionsSkipped > 0 && (
                <li className="gen-stat gen-stat-skipped">
                  <span className="gen-stat-num">{sectionsSkipped}</span>
                  <span className="gen-stat-label">已跳过</span>
                </li>
              )}
            </ul>
          )}

          {job!.phases && Object.keys(job!.phases).length > 0 && (
            <details className="gen-phases-details">
              <summary>查看技术详情</summary>
              <ul className="gen-phases">
                {Object.entries(job!.phases).map(([k, v]) => (
                  <li key={k}>
                    <span className="gen-phase-key">{k}</span>
                    <span className="muted small">
                      {typeof v === 'string' ? v : JSON.stringify(v)}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {job!.sections.length > 0 && (
            <ul className="gen-sections">
              {job!.sections.map((s) => (
                <li
                  key={s.id}
                  className={`gen-section gen-section-${s.status.toLowerCase()}`}
                >
                  <span className="gen-section-type">{s.type}</span>
                  <span className="gen-section-status">{s.status}</span>
                  {s.model_used && (
                    <span className="muted small">{s.model_used}</span>
                  )}
                  {s.total_tokens != null && (
                    <span className="muted small">{s.total_tokens} tokens</span>
                  )}
                  {s.error_code && (
                    <span className="gen-section-err">
                      {s.error_code}
                      {s.error_message ? `：${s.error_message}` : ''}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="ai-build-actions">
            {isRunning && (
              <button
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={busy}
              >
                {busy ? '取消中…' : t('aiBuild.cancelGen')}
              </button>
            )}
            {isTerminal && (job!.status === 'PARTIAL' || job!.status === 'FAILED') && (
              <button
                className="btn btn-secondary"
                onClick={onRetry}
                disabled={busy}
              >
                {busy
                  ? t('aiBuild.generating')
                  : t('aiBuild.retryFailed')}
              </button>
            )}
            {isTerminal && (
              <button
                className="btn btn-primary"
                onClick={onContinue}
                disabled={job!.status === 'FAILED'}
              >
                {t('aiBuild.continueReview')}
              </button>
            )}
          </div>

          {job!.error_message && (
            <div className="callout callout-error">
              <strong>{job!.error_code ?? '错误'}：</strong> {job!.error_message}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =====================================================================
// Step 4 — Review
// =====================================================================

interface ReviewStepProps {
  siteId: string;
  job: SiteJobStatusResponse | null;
  onOpenEditor: () => void;
}

function ReviewStep({job, onOpenEditor}: ReviewStepProps) {
  const {t} = useTranslation();
  const sectionsSuccess =
    job?.sections.filter((s) => s.status === 'SUCCESS').length ?? 0;
  const sectionsFailed =
    job?.sections.filter((s) => s.status === 'FAILED').length ?? 0;
  const sectionsProtected =
    job?.sections.filter((s) => s.human_protected).length ?? 0;
  const allOk = sectionsFailed === 0 && (sectionsSuccess + sectionsProtected) > 0;

  return (
    <div className="card ai-build-card">
      <div className="card-title">
        <span>审核与发布</span>
        <span className="muted small">仅生成草稿，线上站点不受影响</span>
      </div>

      {allOk ? (
        <div className="callout callout-info">
          <strong>{t('aiBuild.draftSaved')}</strong>
          成功 {sectionsSuccess} 个区块
          {sectionsProtected > 0 && `，已保护 ${sectionsProtected} 个`}
        </div>
      ) : sectionsFailed > 0 ? (
        <div className="callout callout-warn">
          <strong>{t('aiBuild.draftPartial')}</strong>
          成功 {sectionsSuccess}、失败 {sectionsFailed}
        </div>
      ) : (
        <div className="callout callout-error">
          <strong>{t('aiBuild.draftFailed')}</strong>
        </div>
      )}

      <ul className="review-summary">
        <li>
          <strong>进入编辑器</strong> —— 修改文案、调整顺序、为单个区块重新生成，或重写常见问题。
        </li>
        <li>
          <strong>预览</strong> —— 在带 token 鉴权的预览地址核对效果（线上站点仍显示上一次发布的版本）。
        </li>
        <li>
          <strong>发布</strong> —— 确认满意后再发布到线上。
        </li>
      </ul>

      <div className="ai-build-actions">
        <button className="btn btn-primary" onClick={onOpenEditor}>
          {t('aiBuild.openEditor')}
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Tiny field components — keep self-contained, no extra CSS
// =====================================================================

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'email' | 'number' | 'tel';
  required?: boolean;
  wide?: boolean;
  hint?: string;
}
function Field({label, value, onChange, type, required, wide, hint}: FieldProps) {
  return (
    <label className={'field ' + (wide ? 'field-wide ' : '')}>
      <span className="field-label">
        {label}
        {required && <em className="req"> *</em>}
      </span>
      <input
        className="field-input"
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="field-hint muted small">{hint}</span>}
    </label>
  );
}

interface AreaProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  wide?: boolean;
  hint?: string;
}
function Area({label, value, onChange, wide, hint}: AreaProps) {
  return (
    <label className={'field ' + (wide ? 'field-wide ' : '')}>
      <span className="field-label">{label}</span>
      <textarea
        className="field-textarea"
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="field-hint muted small">{hint}</span>}
    </label>
  );
}

interface SelectProps {
  label: string;
  value: string;
  options: {value: string; label: string}[];
  onChange: (v: string) => void;
}
function Select({label, value, options, onChange}: SelectProps) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <select
        className="field-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function LanguagesField({
  value,
  onChange,
}: {
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const {t} = useTranslation();
  const [draft, setDraft] = useState('');
  return (
    <div className="field field-wide">
      <span className="field-label">{t('aiBuild.languages')}</span>
      <div className="langs">
        {value.map((l) => (
          <span key={l} className="lang-tag">
            {l}
            <button
              type="button"
              className="lang-remove"
              onClick={() => onChange(value.filter((x) => x !== l))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="field-input lang-input"
          placeholder="添加 BCP-47 语言代码（en、zh、ja…）"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault();
              const v = draft.trim().toLowerCase();
              if (v && !value.includes(v)) onChange([...value, v]);
              setDraft('');
            }
          }}
        />
      </div>
      <span className="field-hint muted small">
        按回车或逗号添加。仅作为内容语言提示。
      </span>
    </div>
  );
}
